import { useCallback, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import SearchInput from '#client/components/SearchInput';
import SelectInput from '#client/components/SelectInput';
import useBreakpoints from '#client/hooks/useBreakpoints';
import { getMonthSelectOptions } from '#client/utils/date';
import { getOptionName } from '#client/utils/transaction';

import { FilterButtonStyled, OptionButtonStyle } from './FilterCheckHistory.styles';

import { MONTH_TRANSACTIONS, SEARCH, TYPE } from '../constants/filter';

const monthOptions = getMonthSelectOptions();

const typeOptions = [
  { key: 'all', name: 'All' },
  { key: 'credit', name: 'Credits' },
  { key: 'debit', name: 'Debits' }
];

export default function FilterTransactions({
  filters,
  searchValue,
  setSearchValue,
  handleFilterCommon,
  handleChangeFilters,
  isDisableButtons,
  handleDesktopFilterChange,
  statementButton,
  setShowMonthOptions,
  setShowTypeOptions
}) {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const trackingSelectMonthEvent = useButtonPressedCallback({
    elementText: 'month',
    elementName: 'tf_month',
    appPageName: 'transaction_history_banking'
  });

  const trackingSelectTypeEvent = useButtonPressedCallback({
    elementText: 'type',
    elementName: 'tf_type',
    appPageName: 'transaction_history_banking'
  });

  const handleEnterToSearch = useCallback(
    (event) => {
      if (event.key === 'Enter' && filters[SEARCH] !== searchValue) {
        setSearchValue(filters[SEARCH]);
        handleFilterCommon();
      }
    },
    [filters, handleFilterCommon, searchValue, setSearchValue]
  );

  const handleInputSearch = useCallback(
    (event) => {
      handleChangeFilters(SEARCH, event.target.value);
    },
    [handleChangeFilters]
  );

  const handleClearSearch = useCallback(() => {
    handleChangeFilters(SEARCH, '');
    if (searchValue) {
      setSearchValue('');
      handleFilterCommon();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const searchInput = useMemo(
    () => (
      <SearchInput
        ariaLabel="search transactions"
        disabled={isDisableButtons}
        handleChange={handleInputSearch}
        handleClear={handleClearSearch}
        handleKeyPress={handleEnterToSearch}
        placeholder="Search"
        value={filters[SEARCH]}
        width={isDesktop ? '320px' : '100%'}
      />
    ),
    [
      isDesktop,
      filters,
      handleInputSearch,
      handleClearSearch,
      handleEnterToSearch,
      isDisableButtons
    ]
  );

  return isDesktop ? (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing="8px"
      >
        {searchInput}
        <Box>
          <SelectInput
            bordered
            disabled={isDisableButtons}
            handleChange={handleDesktopFilterChange(MONTH_TRANSACTIONS)}
            handleClick={trackingSelectMonthEvent}
            Id="month"
            label="Month"
            minWidth={100}
            options={monthOptions}
            value={filters[MONTH_TRANSACTIONS]}
          />
        </Box>
        <Box>
          <SelectInput
            bordered
            disabled={isDisableButtons}
            handleChange={handleDesktopFilterChange(TYPE)}
            handleClick={trackingSelectTypeEvent}
            Id="type"
            label="Type"
            minWidth={100}
            options={typeOptions}
            value={filters[TYPE]}
          />
        </Box>
      </Stack>
      {statementButton}
    </Stack>
  ) : (
    <Stack
      direction="column-reverse"
      spacing="8px"
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing="8px"
        width="100%"
      >
        <FilterButtonStyled
          disabled={isDisableButtons}
          endIcon={
            <SharedAsset
              alt=""
              src="/s2/icon/arrow-sm-down-outline.svg"
            />
          }
          onClick={() => {
            setShowMonthOptions(true);
          }}
          sx={OptionButtonStyle}
          variant="outlined"
        >
          {getOptionName(filters[MONTH_TRANSACTIONS], monthOptions) ?? 'Month'}
        </FilterButtonStyled>
        <FilterButtonStyled
          disabled={isDisableButtons}
          endIcon={
            <SharedAsset
              alt=""
              src="/s2/icon/arrow-sm-down-outline.svg"
            />
          }
          onClick={() => {
            setShowTypeOptions(true);
          }}
          sx={OptionButtonStyle}
          variant="outlined"
        >
          {getOptionName(filters[TYPE], typeOptions) ?? 'Type'}
        </FilterButtonStyled>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing="8px"
        width="100%"
      >
        <Stack flex={1}>{searchInput}</Stack>
        <Stack flex={1}>{statementButton}</Stack>
      </Stack>
    </Stack>
  );
}

FilterTransactions.propTypes = {
  filters: PropTypes.shape({}),
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  handleFilterCommon: PropTypes.func,
  handleChangeFilters: PropTypes.func,
  isDisableButtons: PropTypes.bool,
  handleDesktopFilterChange: PropTypes.func,
  statementButton: PropTypes.node,
  setShowMonthOptions: PropTypes.func,
  setShowTypeOptions: PropTypes.func
};
